import { type ReactNode, useState } from 'react';
import { SpellCard, spellTagsForCard, type SpellCardTag } from '@/components/molecules';
import type { MoveChoice } from '@/schemas/move';
import type { Spell } from '@/schemas/spell';

/**
 * **FI-79 (2026-05-23)** — Per le `MoveChoice` che referenziano spell del
 * catalogo (`mago.archetipo-umano`, `chierico.archetipo-umano`), render
 * con `<SpellCard>` SSOT shared (riusato da SpellPickPanel + FullSheet)
 * invece del label+radio/checkbox classico.
 *
 * Pattern coerente con `SpellPickPanel.PickSpellRow`: SpellCard con
 * `leading=control` (RadioGroupItem o Checkbox shadcn) e
 * `labelHtmlFor=inputId` per area click su tutta la card.
 *
 * Conserva la `option.description` del MoveChoice (es. "Richiede la
 * mossa…") come pillola tag `state` aggiuntiva — vincolo distinto dalla
 * description dello spell (che vive nella SpellCard.body).
 *
 * Estratto da `MoveChoiceGroupsField.tsx` per rispettare `max-lines: 400`.
 */
export interface SpellChoiceCardProps {
  option: MoveChoice;
  spell: Spell;
  inputId: string;
  checked: boolean;
  disabled: boolean;
  /** Control widget (RadioGroupItem o Checkbox shadcn) passato come `leading` slot. */
  control: ReactNode;
}

export function SpellChoiceCard({
  option,
  spell,
  inputId,
  checked,
  disabled,
  control,
}: SpellChoiceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const tags: SpellCardTag[] = [
    ...spellTagsForCard(spell, checked ? 'picked' : 'pick'),
    ...(option.description ? [{ label: option.description, variant: 'state' as const }] : []),
  ];
  return (
    <SpellCard
      spell={spell}
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      tags={tags}
      leading={control}
      labelHtmlFor={inputId}
      selected={checked}
      disabled={disabled}
    />
  );
}
